import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input } from "antd";
import { Check, ChevronRight, Copy, Search } from "lucide-react";
import { copyToClipboard } from "@/utility/copyToClipboard";

export interface CustomProperty {
  label: string;
  value: unknown;
}

interface PropertyEntry {
  label: string;
  parsed: unknown;
  isComplex: boolean;
  count: number;
  text: string;
  lowerLabel: string;
  lowerText: string;
}

const COLLECTION_LABELS = new Set(["Generated Sections", "Attached Files"]);
const LONG_VALUE_THRESHOLD = 140;
const COPIED_FEEDBACK_MS = 1500;
// A broad query on a large property would otherwise create thousands of nodes.
const MAX_HIGHLIGHTS_PER_VALUE = 200;

function toEntry(prop: CustomProperty): PropertyEntry {
  let parsed: unknown = prop.value;
  // Only objects and arrays are decoded: JSON.parse would also turn an ID such
  // as "12345678901234567890" or "1e3" into a different number.
  if (typeof parsed === "string" && /^\s*[[{]/.test(parsed)) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      // not JSON after all; keep the original text
    }
  }

  if (parsed === null || parsed === undefined || parsed === "null") {
    parsed = COLLECTION_LABELS.has(prop.label) ? [] : "";
  }

  const isComplex = typeof parsed === "object" && parsed !== null;
  const text = isComplex ? JSON.stringify(parsed, null, 2) : String(parsed);
  return {
    label: prop.label,
    parsed,
    isComplex,
    count: isComplex ? Object.keys(parsed as object).length : 0,
    text,
    lowerLabel: prop.label.toLowerCase(),
    lowerText: text.toLowerCase(),
  };
}

/** Wraps each case-insensitive occurrence of `needle` in a highlight. */
function highlightMatches(text: string, lowerText: string, needle: string): React.ReactNode {
  if (!needle) return text;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let found = lowerText.indexOf(needle);
  let highlighted = 0;
  while (found !== -1 && highlighted < MAX_HIGHLIGHTS_PER_VALUE) {
    if (found > cursor) parts.push(text.slice(cursor, found));
    parts.push(
      <mark className="settings_props_mark" key={found}>
        {text.slice(found, found + needle.length)}
      </mark>
    );
    cursor = found + needle.length;
    highlighted += 1;
    found = lowerText.indexOf(needle, cursor);
  }
  if (cursor === 0) return text;
  parts.push(text.slice(cursor));
  return parts;
}

function CopyButton({ text, label, onCopied }: { text: string; label: string; onCopied: (ok: boolean) => void }) {
  const [copied, setCopied] = useState(false);

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const ok = await copyToClipboard(text);
    onCopied(ok);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <button
      type="button"
      className={`settings_props_icon_button ${copied ? "copied" : ""}`}
      aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
      title={copied ? "Copied" : "Copy"}
      onClick={handleClick}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

function CustomPropertiesList({ properties }: { properties: CustomProperty[] }) {
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [expandedText, setExpandedText] = useState<Set<string>>(new Set());
  const [copyFailed, setCopyFailed] = useState(false);

  const entries = useMemo(() => properties.map(toEntry), [properties]);

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? entries.filter((entry) => entry.lowerLabel.includes(needle) || entry.lowerText.includes(needle))
    : entries;

  // Bring the first match into view: a match deep inside a long value would
  // otherwise sit below the fold of its scrolling box.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!needle) return;
    listRef.current?.querySelector("mark")?.scrollIntoView?.({ block: "nearest" });
  }, [needle]);

  const toggle = (set: Set<string>, setter: (next: Set<string>) => void, key: string) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };

  const copyAllText = useMemo(
    () => JSON.stringify(Object.fromEntries(entries.map((entry) => [entry.label, entry.parsed])), null, 2),
    [entries]
  );

  return (
    <div className="settings_props">
      <div className="settings_props_toolbar">
        <Input
          size="small"
          allowClear
          className="settings_props_search"
          placeholder="Search keys and values"
          aria-label="Search custom properties"
          prefix={<Search size={13} className="settings_props_search_icon" />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button
          size="small"
          icon={<Copy size={12} />}
          onClick={async () => setCopyFailed(!(await copyToClipboard(copyAllText)))}
        >
          Copy all
        </Button>
      </div>
      {copyFailed && <div className="settings_props_error ui-text-meta">Could not copy to the clipboard.</div>}

      {visible.length === 0 && <div className="settings_props_empty ui-text-body">No properties match “{query}”.</div>}

      <div className="settings_props_list" ref={listRef}>
        {visible.map((entry) => {
          const matchesInValue = needle !== "" && entry.lowerText.includes(needle);
          const canExpand = entry.isComplex && entry.count > 0;
          const isOpen = canExpand && (openKeys.has(entry.label) || matchesInValue);
          const isLong = !entry.isComplex && entry.text.length > LONG_VALUE_THRESHOLD;
          const isTextExpanded = expandedText.has(entry.label) || matchesInValue;

          return (
            <div className="settings_props_item" key={entry.label}>
              <div
                className={`settings_props_item_header ${canExpand ? "expandable" : ""}`}
                onClick={canExpand ? () => toggle(openKeys, setOpenKeys, entry.label) : undefined}
                aria-expanded={canExpand ? isOpen : undefined}
              >
                {canExpand && <ChevronRight size={13} className={`settings_props_chevron ${isOpen ? "open" : ""}`} />}
                <span className="settings_props_key">
                  {needle && entry.lowerLabel.includes(needle)
                    ? highlightMatches(entry.label, entry.lowerLabel, needle)
                    : entry.label}
                </span>
                {entry.isComplex && (
                  <span className="settings_props_badge">
                    {Array.isArray(entry.parsed) ? `${entry.count} items` : `${entry.count} keys`}
                  </span>
                )}
                {entry.text !== "" && !(entry.isComplex && entry.count === 0) && (
                  <CopyButton text={entry.text} label={entry.label} onCopied={(ok) => setCopyFailed(!ok)} />
                )}
              </div>

              {!entry.isComplex && (
                <div
                  className={`settings_props_value ${isLong && !isTextExpanded ? "clamped" : ""} ${
                    entry.text === "" ? "empty" : ""
                  }`}
                >
                  {entry.text === "" ? "—" : highlightMatches(entry.text, entry.lowerText, needle)}
                </div>
              )}
              {isLong && (
                <button
                  type="button"
                  className="settings_props_more"
                  onClick={() => toggle(expandedText, setExpandedText, entry.label)}
                >
                  {isTextExpanded ? "Show less" : "Show more"}
                </button>
              )}
              {isOpen && (
                <pre className="settings_props_json">{highlightMatches(entry.text, entry.lowerText, needle)}</pre>
              )}
              {entry.isComplex && entry.count === 0 && <div className="settings_props_value empty">{"—"}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CustomPropertiesList;
